/**
 * COSO Documents Tab Component
 * 
 * Document repository interface for COSO Compliance module
 * Features:
 * - File upload (drag & drop + button)
 * - Document list with metadata
 * - Download/Delete actions
 * - File type icons
 * - API integration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Upload, message } from 'antd';
import type { UploadProps } from 'antd';
import { 
  FileOutlined, 
  FilePdfOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  DownloadOutlined,
  DeleteOutlined,
  InboxOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { RootState, AppDispatch } from '../../../store';
import { 
  setDocuments, 
  addDocument, 
  removeDocument,
  setLoadingDocuments 
} from '../../../store/slices/cosoComplianceSlice';
import { 
  getCOSOComplianceDocuments,
  formatFileSize,
  ParsedCOSODocument,
  parseCOSODocument,
} from '../../../services/cosoComplianceService';
import { 
  uploadCOSOComplianceDocument, 
  deleteCOSOComplianceDocument,
  downloadFile 
} from '../../../services/fileService';
import ConfirmModal from '../../../components/common/ConfirmModal';
import trashIcon from '../../../assets/icons/ic-trash.svg';
import { ALLOWED_FILE_EXTENSIONS } from '../../../utils/constants';

const { Dragger } = Upload;

// ========== STYLED COMPONENTS ==========

const DocumentsContainer = styled.div`
  padding: 24px;
  background: white;
  border-radius: 8px;
`;

const UploadSection = styled.div`
  margin-bottom: 24px;
`;

const StyledDragger = styled(Dragger)`
  &.ant-upload-wrapper .ant-upload-drag {
    background: #FAFAFA;
    border: 2px dashed #D1D5DB;
    border-radius: 8px;
    transition: all 0.3s;
    
    &:hover {
      border-color: #2563EB;
    }
  }
  
  &.ant-upload-wrapper .ant-upload-drag .ant-upload-btn {
    padding: 32px 16px;
  }
  
  .ant-upload-drag-icon {
    margin-bottom: 16px;
    
    .anticon {
      font-size: 48px;
      color: #2563EB;
    }
  }
  
  .ant-upload-text {
    font-family: Inter;
    font-weight: 500;
    font-size: 16px;
    color: #1F2937;
    margin-bottom: 8px;
  }
  
  .ant-upload-hint {
    font-family: Inter;
    font-size: 14px;
    color: #6B7280;
  }
`;

const SectionTitle = styled.h3`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  color: #1F2937;
  margin-bottom: 16px;
`;

const DocumentsWrapper = styled.div`
  background: #E0E7FF;
  border-radius: 6px;
  border: 1px solid #E0E7FF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  padding: 2px;
`;

const DocumentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 16px;
  background: #FFFFFF;
  border-radius: 4px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DocumentCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  transition: all 0.2s;
  
  &:hover {
    border-color: #2563EB;
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.1);
  }
`;

const DocumentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const FileIcon = styled.div<{ $type: string }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 20px;
  
  ${({ $type }) => {
    switch ($type) {
      case 'pdf':
        return `
          background: #FEE2E2;
          color: #DC2626;
        `;
      case 'doc':
      case 'docx':
        return `
          background: #DBEAFE;
          color: #2563EB;
        `;
      case 'xls':
      case 'xlsx':
        return `
          background: #D1FAE5;
          color: #059669;
        `;
      default:
        return `
          background: #F3F4F6;
          color: #6B7280;
        `;
    }
  }}
`;

const FileDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1F2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSize = styled.div`
  font-family: Inter;
  font-size: 12px;
  color: #9CA3AF;
  margin-top: 2px;
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ $variant?: 'download' | 'delete'; $loading?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: #F3F4F6;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  ${({ $variant }) => {
    if ($variant === 'delete') {
      return `
        color: #6B7280;
        &:hover:not(:disabled) {
          background: #FEE2E2;
          color: #DC2626;
        }
      `;
    }
    return `
      color: #6B7280;
      &:hover:not(:disabled) {
        background: #E5E7EB;
        color: #2563EB;
      }
    `;
  }}
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
  
  ${({ $loading }) => $loading && `
    color: #2563EB;
  `}
`;

const SpinIcon = styled(LoadingOutlined)`
  font-size: 16px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  min-height: 400px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 6px;
`;

const EmptyStateText = styled.div`
  color: #1F2937;
  font-family: Inter;
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 16px;
`;

// ========== HELPER FUNCTIONS ==========

const getFileIcon = (type: string) => {
  switch (type) {
    case 'pdf':
      return <FilePdfOutlined />;
    case 'xls':
    case 'xlsx':
      return <FileExcelOutlined />;
    case 'doc':
    case 'docx':
      return <FileWordOutlined />;
    default:
      return <FileOutlined />;
  }
};

// ========== MAIN COMPONENT ==========

const COSODocumentsTab: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  
  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<ParsedCOSODocument | null>(null);
  
  // Get data from Redux
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const { documents, documentsLastFetchedReportId, isLoadingDocuments } = useSelector(
    (state: RootState) => state.cosoCompliance
  );

  // Fetch documents on mount or when report changes
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!selectedReport?.annualReportId) {
        return;
      }
      
      // Check Redux cache - skip fetch if data already loaded for this report
      if (documentsLastFetchedReportId === selectedReport.annualReportId) {
        // console.log('⏭️ COSODocumentsTab: Using cached data for:', selectedReport.annualReportId);
        return;
      }
      
      dispatch(setLoadingDocuments(true));
      
      try {
        // console.log('📁 COSODocumentsTab: Fetching documents...');
        const response = await getCOSOComplianceDocuments(selectedReport.annualReportId);
        
        dispatch(setDocuments({
          documents: response.data,
          reportId: selectedReport.annualReportId,
        }));
        
        // console.log('✅ COSODocumentsTab: Documents loaded:', response.data.length);
      } catch (error: any) {
        console.error('Error fetching documents:', error);
        message.error(error?.message || 'Failed to load documents');
      } finally {
        dispatch(setLoadingDocuments(false));
      }
    };
    
    fetchDocuments();
  }, [selectedReport?.annualReportId, documentsLastFetchedReportId, dispatch]);

  // Handle file upload
  const handleUpload = useCallback(async (file: File) => {
    if (!selectedReport?.annualReportId) {
      message.error('No annual report selected');
      return false;
    }
    
    setUploading(true);
    try {
      // console.log('📤 Uploading file:', file.name);
      const response = await uploadCOSOComplianceDocument(selectedReport.annualReportId, file);
      
      if (response.data?.succeeded) {
        // Create parsed document to add to Redux
        const newDoc: ParsedCOSODocument = parseCOSODocument({
          Id: response.data.id,
          FileName: file.name,
          FileSize: file.size,
          CreatedAt: new Date().toISOString(),
        });
        
        dispatch(addDocument(newDoc));
        message.success(`${file.name} uploaded successfully`);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error: any) {
      console.error('Upload failed:', error);
      message.error(error?.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
    
    return false; // Prevent default upload behavior
  }, [selectedReport?.annualReportId, dispatch]);

  // Handle download using fileService
  const handleDownload = useCallback(async (doc: ParsedCOSODocument) => {
    try {
      setDownloadingId(doc.id);
      // console.log('📥 Downloading file:', doc.fileName, 'ID:', doc.id);
      
      await downloadFile(doc.id, doc.fileName);
      
      message.success(`Downloaded ${doc.fileName}`);
    } catch (error: any) {
      console.error('Download failed:', error);
      message.error(error?.message || `Failed to download ${doc.fileName}`);
    } finally {
      setDownloadingId(null);
    }
  }, []);

  // Open delete confirmation modal
  const openDeleteModal = useCallback((doc: ParsedCOSODocument) => {
    setDocumentToDelete(doc);
    setDeleteModalOpen(true);
  }, []);
  
  // Close delete confirmation modal
  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setDocumentToDelete(null);
  }, []);

  // Handle delete confirmation
  const handleConfirmDelete = useCallback(async () => {
    if (!selectedReport?.annualReportId || !documentToDelete) {
      message.error('No annual report selected');
      return;
    }
    
    setDeletingId(documentToDelete.id);
    try {
      // console.log('🗑️ Deleting file:', documentToDelete.fileName, 'ID:', documentToDelete.id);
      await deleteCOSOComplianceDocument(selectedReport.annualReportId, documentToDelete.id);
      
      dispatch(removeDocument(documentToDelete.id));
      message.success('Document deleted successfully');
      closeDeleteModal();
    } catch (error: any) {
      console.error('Delete failed:', error);
      message.error(error?.message || 'Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  }, [selectedReport?.annualReportId, documentToDelete, dispatch, closeDeleteModal]);

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    showUploadList: false,
    beforeUpload: (file) => {
      handleUpload(file);
      return false;
    },
    accept: ALLOWED_FILE_EXTENSIONS,
  };

  if (isLoadingDocuments) {
    return (
      <DocumentsContainer>
        <LoadingContainer>
          <div className="spinner" />
        </LoadingContainer>
      </DocumentsContainer>
    );
  }

  return (
    <DocumentsContainer>
      {/* Upload Section */}
      <UploadSection>
        <SectionTitle>Upload Documents</SectionTitle>
        <StyledDragger {...uploadProps} disabled={uploading}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {uploading ? 'Uploading...' : 'Click or drag file to this area to upload'}
          </p>
          <p className="ant-upload-hint">
            Support for PDF, Word, Excel, PowerPoint, Visio, and other document formats. Max file size: 50MB
          </p>
        </StyledDragger>
      </UploadSection>

      {/* Documents List */}
      <div>
        <SectionTitle>Documents Repository ({documents.length})</SectionTitle>
        {documents.length > 0 ? (
          <DocumentsWrapper>
            <DocumentsGrid>
              {documents.map((doc) => (
                <DocumentCard key={doc.id}>
                  <DocumentInfo>
                    <FileIcon $type={doc.fileType}>
                      {getFileIcon(doc.fileType)}
                    </FileIcon>
                    <FileDetails>
                      <FileName title={doc.fileName}>{doc.fileName}</FileName>
                      <FileSize>{formatFileSize(doc.fileSize)}</FileSize>
                    </FileDetails>
                  </DocumentInfo>
                  <ActionsCell>
                    <ActionButton
                      onClick={() => handleDownload(doc)}
                      disabled={downloadingId === doc.id}
                      $loading={downloadingId === doc.id}
                      title="Download"
                    >
                      {downloadingId === doc.id ? <SpinIcon /> : <DownloadOutlined />}
                    </ActionButton>
                    <ActionButton
                      $variant="delete"
                      disabled={deletingId === doc.id}
                      $loading={deletingId === doc.id}
                      title="Delete"
                      onClick={() => openDeleteModal(doc)}
                    >
                      {deletingId === doc.id ? <SpinIcon /> : <DeleteOutlined />}
                    </ActionButton>
                  </ActionsCell>
                </DocumentCard>
              ))}
            </DocumentsGrid>
          </DocumentsWrapper>
        ) : (
          <EmptyStateWrapper>
            <EmptyStateText>No documents uploaded yet</EmptyStateText>
          </EmptyStateWrapper>
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        icon={trashIcon}
        title="Delete Document"
        description={
          <>
            Are you sure you want to delete <strong>{documentToDelete?.fileName}</strong>?
            <br />
            This action cannot be undone.
          </>
        }
        confirmText="Delete"
        cancelText="Cancel"
        danger={true}
        loading={deletingId === documentToDelete?.id}
      />
    </DocumentsContainer>
  );
};

export default COSODocumentsTab;
